/**
 * ReportIssueModal Component
 * Sprint 28: Report facts about order issues (OOS, vendor closed, buyer unreachable)
 * 
 * Rider reports FACTS ONLY — rider never decides refunds or replacements.
 * After reporting, rider CANNOT complete or cancel the order.
 */

import { useState } from 'react';
import { X, AlertTriangle, Store, Package, User, MapPin } from 'lucide-react';

export type IssueLocation = 'pickup' | 'dropoff';

export type PickupIssueType = 
  | 'vendor_closed'
  | 'out_of_stock'
  | 'partial_quantity'
  | 'wrong_item'
  | 'vendor_refused'
  | 'other_pickup';

export type DropoffIssueType = 
  | 'buyer_unreachable'
  | 'wrong_address'
  | 'buyer_refused'
  | 'access_issue'
  | 'other_dropoff';

export type IssueType = PickupIssueType | DropoffIssueType;

export interface IssueReport {
  orderId: string;
  location: IssueLocation;
  issueType: IssueType;
  notes?: string;
  affectedItems?: string[]; // For OOS/partial reports
}

interface ReportIssueModalProps {
  orderId: string;
  location: IssueLocation;
  onSubmit: (report: IssueReport) => Promise<void>;
  onClose: () => void;
}

const pickupIssues: { type: PickupIssueType; label: string; icon: React.ReactNode }[] = [
  { type: 'vendor_closed', label: 'Vendor is closed', icon: <Store className="w-5 h-5" /> },
  { type: 'out_of_stock', label: 'Items out of stock', icon: <Package className="w-5 h-5" /> },
  { type: 'partial_quantity', label: 'Partial quantity available', icon: <Package className="w-5 h-5" /> },
  { type: 'wrong_item', label: 'Wrong item prepared', icon: <AlertTriangle className="w-5 h-5" /> },
  { type: 'vendor_refused', label: 'Vendor refused to release', icon: <X className="w-5 h-5" /> },
  { type: 'other_pickup', label: 'Other issue at pickup', icon: <AlertTriangle className="w-5 h-5" /> },
];

const dropoffIssues: { type: DropoffIssueType; label: string; icon: React.ReactNode }[] = [
  { type: 'buyer_unreachable', label: 'Buyer not responding', icon: <User className="w-5 h-5" /> },
  { type: 'wrong_address', label: 'Wrong or invalid address', icon: <MapPin className="w-5 h-5" /> },
  { type: 'buyer_refused', label: 'Buyer refused delivery', icon: <X className="w-5 h-5" /> },
  { type: 'access_issue', label: 'Cannot access location', icon: <MapPin className="w-5 h-5" /> },
  { type: 'other_dropoff', label: 'Other issue at dropoff', icon: <AlertTriangle className="w-5 h-5" /> },
];

export default function ReportIssueModal({
  orderId,
  location,
  onSubmit,
  onClose,
}: ReportIssueModalProps) {
  const [selectedIssue, setSelectedIssue] = useState<IssueType | null>(null);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const issues = location === 'pickup' ? pickupIssues : dropoffIssues;

  const handleSubmit = async () => {
    if (!selectedIssue) {
      setError('Please select an issue type');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const report: IssueReport = {
        orderId,
        location,
        issueType: selectedIssue,
        notes: notes.trim() || undefined,
      };

      await onSubmit(report);
    } catch (err: any) {
      setError(err.message || 'Failed to submit report');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl max-w-md w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold">Report Issue</h2>
            <p className="text-sm text-muted-foreground">
              {location === 'pickup' ? 'At Pickup Location' : 'At Dropoff Location'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Issue Selection */}
        <div className="p-4 space-y-2 max-h-[50vh] overflow-y-auto">
          {issues.map(({ type, label, icon }) => (
            <button
              key={type}
              onClick={() => {
                setSelectedIssue(type);
                setError(null);
              }}
              className={`w-full p-4 rounded-xl border-2 transition-colors flex items-center gap-3 text-left ${
                selectedIssue === type
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                selectedIssue === type ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600'
              }`}>
                {icon}
              </div>
              <span className="font-medium">{label}</span>
            </button>
          ))}
        </div>

        {/* Notes */}
        <div className="p-4 border-t">
          <label className="block text-sm font-medium mb-2">
            Additional Notes (optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Describe the situation..."
            className="w-full p-3 border rounded-xl resize-none focus:ring-2 focus:ring-primary focus:border-primary"
            rows={3}
          />
        </div>

        {/* Error */}
        {error && (
          <div className="px-4 pb-2">
            <div className="p-3 bg-red-50 text-red-700 rounded-xl text-sm">
              {error}
            </div>
          </div>
        )}

        {/* Important Notice */}
        <div className="px-4 pb-2">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-amber-800">
                <strong>Important:</strong> After reporting, you will need to wait for 
                instructions. You cannot complete or cancel this order until the issue is resolved.
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 flex gap-3">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 py-3 px-4 border rounded-xl font-medium hover:bg-muted transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedIssue || isSubmitting}
            className="flex-1 py-3 px-4 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              'Submit Report'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
